/**
 * HustleRoundupRecordingPage — playback + transcript viewer.
 *
 * Route: /recording/:id
 * Fetches: /api/hustleroundup/recordings/:id  +  parent meeting transcript
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Download, Sparkles, RefreshCcw, FileText, ListChecks } from "lucide-react";
import { toast } from "sonner";
import { apiFetch, fetchMe } from "@/lib/api";

export default function HustleRoundupRecordingPage() {
  const { id } = useParams();
  const nav    = useNavigate();
  const [rec, setRec] = useState(null);
  const [trans, setTrans] = useState(null);
  const [err, setErr] = useState(null);
  const [regen, setRegen] = useState(false);

  const load = async () => {
    try {
      const r = await apiFetch(`/hustleroundup/recordings/${id}`);
      setRec(r);
      if (r?.meeting_id) {
        try {
          const t = await apiFetch(`/hustleroundup/meetings/${r.meeting_id}/transcript`);
          setTrans(t);
        } catch (e) {
          if (e.status !== 404) throw e;
          setTrans(null);
        }
      }
    } catch (e) { setErr(e.message || "Failed to load"); }
  };

  useEffect(() => {
    (async () => {
      const u = await fetchMe();
      if (!u?.id) { nav("/login?next=/recording/" + id); return; }
      await load();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const requeue = async () => {
    if (!rec?.meeting_id) return;
    setRegen(true);
    try {
      await apiFetch(`/hustleroundup/meetings/${rec.meeting_id}/transcript`, { method: "POST" });
      toast.success("Transcript job queued");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.message); }
    finally { setRegen(false); }
  };

  const actionItems = useMemo(() => trans?.action_items || [], [trans]);
  const decisions   = useMemo(() => trans?.key_decisions || [], [trans]);

  if (err) {
    return (
      <div className="min-h-screen bg-stone-950 text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <div className="text-xl font-bold mb-2">{err}</div>
          <button onClick={() => nav("/history")} className="mt-4 px-4 py-2 rounded-full bg-emerald-500 text-stone-950 font-semibold text-sm">
            Back to history
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="hustleroundup-recording"
         className="min-h-screen bg-stone-950 text-white px-6 py-8">
      <div className="max-w-6xl mx-auto">
        <button data-testid="recording-back" onClick={() => nav("/history")}
                className="text-stone-400 hover:text-white text-sm flex items-center gap-2 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to history
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Player */}
          <div>
            <div className="rounded-2xl border border-stone-900 bg-black overflow-hidden aspect-video">
              {rec?.public_url && rec?.state === "ready" ? (
                <video data-testid="hustleroundup-recording-player"
                       src={rec.public_url}
                       controls
                       className="w-full h-full object-contain bg-black"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-500 text-sm">
                  {rec?.state === "failed"
                    ? "Recording failed"
                    : `Recording ${rec?.state || "…"}${rec?.state === "uploading" ? " — try again shortly" : ""}`}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-stone-400">
              <span>Rec <code className="text-emerald-400">{rec?.id}</code></span>
              <span>· state <code>{rec?.state || "…"}</code></span>
              {rec?.duration_s ? <span>· {Math.round(rec.duration_s)}s</span> : null}
              {rec?.public_url && rec.state === "ready" && (
                <a data-testid="recording-download"
                   href={rec.public_url} target="_blank" rel="noreferrer"
                   className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-700 hover:border-emerald-500">
                  <Download className="w-3.5 h-3.5" /> Download MP4
                </a>
              )}
            </div>
          </div>

          {/* Transcript pane */}
          <aside data-testid="hustleroundup-transcript-pane"
                 className="rounded-2xl border border-stone-900 bg-stone-900/40 p-5 h-max">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <div className="font-semibold text-sm">Transcript & summary</div>
              <button
                data-testid="transcript-refresh"
                onClick={load}
                className="ml-auto p-1.5 rounded-md text-stone-400 hover:text-white"
                aria-label="Refresh"
              ><RefreshCcw className="w-4 h-4" /></button>
            </div>

            {!trans && (
              <div className="text-xs text-stone-500 mb-3">
                No transcript yet.
                <button data-testid="transcript-enqueue"
                        onClick={requeue}
                        disabled={regen}
                        className="ml-2 px-2 py-1 rounded-md bg-emerald-500 text-stone-950 text-[11px] font-semibold disabled:opacity-60">
                  {regen ? "Queueing…" : "Generate now"}
                </button>
              </div>
            )}
            {trans && trans.state !== "ready" && (
              <div className="text-xs text-amber-300 mb-3">Status: {trans.state}</div>
            )}
            {trans?.state === "ready" && (
              <>
                <SectionTitle icon={FileText}>Summary</SectionTitle>
                <div data-testid="transcript-summary" className="text-sm text-stone-200 whitespace-pre-wrap mb-4 leading-relaxed">
                  {trans.summary}
                </div>
                {decisions?.length > 0 && (
                  <>
                    <SectionTitle icon={ListChecks}>Key decisions</SectionTitle>
                    <ul data-testid="transcript-decisions" className="list-disc pl-4 space-y-1 text-sm mb-4">
                      {decisions.map((d, i) => <li key={i}>{d}</li>)}
                    </ul>
                  </>
                )}
                {actionItems?.length > 0 && (
                  <>
                    <SectionTitle icon={ListChecks}>Action items</SectionTitle>
                    <ul data-testid="transcript-actions" className="space-y-2 text-sm mb-4">
                      {actionItems.map((a, i) => (
                        <li key={i} className="p-2 rounded-md bg-stone-950 border border-stone-800">
                          <div className="font-semibold">{a.owner || "—"}</div>
                          <div className="text-stone-300">{a.task}</div>
                          {a.due && <div className="text-[11px] text-stone-500 mt-0.5">due {a.due}</div>}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                <details className="text-xs text-stone-400" data-testid="transcript-raw">
                  <summary className="cursor-pointer hover:text-white">Raw transcript</summary>
                  <pre className="whitespace-pre-wrap mt-2 max-h-64 overflow-auto">{trans.transcript}</pre>
                </details>
              </>
            )}
            {trans?.error && (
              <div className="text-xs text-red-400 mt-2">Error: {trans.error}</div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-400 mb-1">
      <Icon className="w-3 h-3" />
      {children}
    </div>
  );
}
